import { useState } from 'react';
import { Link } from 'react-router';

import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { deliveryFieldHelp } from '../../app/guidance/fieldHelpRegistry';
import { Card, Page } from '../../app/layout/Page';
import { useUnitDisplay, type QuantityKind } from '../../app/units/UnitDisplayContext';
import { SolutionDetails } from '../optimization/SolutionDetails';
import type { OptimizationRunRecord } from '../optimization/types';
import type {
  SavedConstraintResult,
  SavedResultsSnapshot,
  SavedSimulationView,
} from './types';

import '../optimization/optimization.css';
import './results.css';

export interface ResultsWorkspaceProps {
  snapshot: SavedResultsSnapshot;
}

function constraintQuantity(measurement: string): QuantityKind | null {
  const quantities: Record<string, QuantityKind> = {
    flow: 'flowRate',
    head: 'head',
    power: 'power',
    pressure: 'pressure',
  };
  return quantities[measurement] ?? null;
}

function constraintValue(
  item: SavedConstraintResult,
  formatSiValue: (value: number, quantity: QuantityKind) => string,
): string {
  const quantity = constraintQuantity(item.measurement);
  const format = (value: number | null) => {
    if (value === null) return 'not evaluated';
    if (quantity !== null) return formatSiValue(value, quantity);
    if (item.measurement === 'fraction') return `${(value * 100).toFixed(1)}%`;
    if (item.measurement === 'speed') return `${value.toFixed(0)} rpm`;
    return value.toFixed(3);
  };
  return [
    `actual ${format(item.actual)}`,
    `limit ${format(item.limit)}`,
    `margin ${format(item.margin)}`,
  ].join(' · ');
}

function proofText(run: OptimizationRunRecord): string {
  if (run.recommended_solution !== null) {
    if (run.minimum_count_proven) {
      return run.secondary_objectives_proven
        ? 'Minimum pump count and secondary ordering were proven.'
        : 'Minimum pump count was proven; secondary ordering is best found.';
    }
    return 'Best feasible design found; optimality was not proven.';
  }
  if (run.conclusion === 'proven_infeasible') {
    return 'Proven infeasible only within the recorded bounded search space.';
  }
  return 'No feasible design found yet; infeasibility was not proven.';
}

function optimizationOutcome(run: OptimizationRunRecord): string {
  if (run.recommended_solution !== null) return 'Meets requirement';
  if (run.conclusion === 'proven_infeasible') {
    return 'Does not meet requirement';
  }
  return 'No feasible result yet';
}

function readableState(value: string): string {
  return value.replaceAll('_', ' ');
}

function resultOutcome(simulation: SavedSimulationView): {
  instruction: string;
  label: string;
  tone: 'danger' | 'success' | 'warning';
} {
  if (simulation.state !== 'completed') {
    return {
      instruction: 'Next: review solver messages.',
      label: `Run ${readableState(simulation.state)}`,
      tone: 'warning',
    };
  }
  if (!simulation.converged) {
    return {
      instruction: 'Next: review solver messages.',
      label: 'Not converged',
      tone: 'danger',
    };
  }
  if (!simulation.feasible) {
    return {
      instruction: 'Next: review failed and unevaluated checks.',
      label: 'Not feasible',
      tone: 'danger',
    };
  }
  return {
    instruction: 'Next: export the saved result.',
    label: 'Feasible',
    tone: 'success',
  };
}

function findingsSummary(simulation: SavedSimulationView): string {
  const failed =
    simulation.constraints.filter((item) => item.state === 'failed').length +
    simulation.failures.length;
  const notEvaluated = simulation.constraints.filter(
    (item) => item.state === 'not_evaluated',
  ).length;
  const passed = simulation.constraints.filter((item) => item.state === 'passed').length;
  if (failed + notEvaluated + passed === 0) return 'No saved checks.';
  if (failed === 0 && notEvaluated === 0) {
    return passed === 1 ? 'The saved check passed.' : `All ${passed} saved checks passed.`;
  }
  return `${failed} failed · ${notEvaluated} not evaluated · ${passed} passed`;
}

export function ResultsWorkspace({ snapshot }: ResultsWorkspaceProps) {
  const { formatSiValue } = useUnitDisplay();
  const [selection, setSelection] = useState({
    projectId: snapshot.project_id,
    simulationId: snapshot.simulations[0]?.id ?? '',
  });
  const requestedId =
    selection.projectId === snapshot.project_id ? selection.simulationId : '';
  const simulation =
    snapshot.simulations.find((item) => item.id === requestedId) ??
    snapshot.simulations[0] ??
    null;
  const outcome = simulation === null ? null : resultOutcome(simulation);
  const visibleConstraints =
    simulation?.constraints.filter((item) => item.state !== 'passed') ?? [];
  const passedConstraints =
    simulation?.constraints.filter((item) => item.state === 'passed') ?? [];

  return (
    <Page title={`${snapshot.project_name} results`}>
      <div className="results-workspace">
        {snapshot.simulations.length === 0 ? (
          <Card title="No saved simulation results">
            <p>Save a simulation before creating result-dependent exports.</p>
          </Card>
        ) : (
          <>
            <label className="results-selector">
              Saved scenario
              <select
                {...guided(deliveryFieldHelp.savedResult)}
                value={simulation?.id ?? ''}
                onChange={(event) =>
                  setSelection({
                    projectId: snapshot.project_id,
                    simulationId: event.target.value,
                  })
                }
              >
                {snapshot.simulations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.scenario_name} · {readableState(item.state)}
                  </option>
                ))}
              </select>
            </label>

            {simulation === null || outcome === null ? null : (
              <>
                <Card className={`results-outcome results-outcome--${outcome.tone}`}>
                  <div className="results-outcome__header">
                    <div>
                      <h2>{simulation.scenario_name}</h2>
                      <p className="results-outcome__state" role="status">
                        {outcome.label}
                      </p>
                      <p>{outcome.instruction}</p>
                    </div>
                    <div className="button-row results-actions">
                      {simulation.feasible ? (
                        <>
                          <Link className="button button--primary" to="/exports">
                            Export saved result
                          </Link>
                          <a className="button button--secondary" href="#results-findings">
                            Review checks
                          </a>
                        </>
                      ) : (
                        <>
                          <a className="button button--primary" href="#results-findings">
                            Review checks
                          </a>
                          <Link className="button button--secondary" to="/exports">
                            Export saved result
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </Card>

                <div id="results-findings">
                  <Card title="Checks">
                    <p className="results-findings-summary">
                      {findingsSummary(simulation)}
                    </p>
                    {visibleConstraints.length + simulation.failures.length === 0 ? null : (
                      <ul className="results-findings">
                        {visibleConstraints.map((item, index) => (
                          <li
                            key={`${item.type}-${item.entity_id ?? 'global'}-${index}`}
                            className={`results-finding--${item.state}`}
                          >
                            <strong>
                              {readableState(item.type)} · {readableState(item.state)}
                            </strong>
                            <span>{constraintValue(item, formatSiValue)}</span>
                            <span>{item.message}</span>
                          </li>
                        ))}
                        {simulation.failures.map((item) => (
                          <li
                            key={`${item.code}-${item.message}`}
                            className="results-finding--failed"
                          >
                            <strong>Solver message · {readableState(item.code)}</strong>
                            <span>{item.message}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {passedConstraints.length === 0 ? null : (
                      <details className="results-passed-checks">
                        <summary>Passed checks ({passedConstraints.length})</summary>
                        <ul className="results-findings">
                          {passedConstraints.map((item, index) => (
                            <li
                              key={`passed-${item.type}-${item.entity_id ?? 'global'}-${index}`}
                              className="results-finding--passed"
                            >
                              <strong>{readableState(item.type)} · passed</strong>
                              <span>{constraintValue(item, formatSiValue)}</span>
                              <span>{item.message}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </Card>
                </div>

                <Card title="Pump operating points">
                  {simulation.pumps.length === 0 ? (
                    <p>No enabled pump operating points were saved.</p>
                  ) : (
                    <div className="results-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Unit / site</th>
                            <th>Engine / pump RPM</th>
                            <th>Flow</th>
                            <th>Head</th>
                            <th>Power</th>
                            <th>NPSHa / NPSHr / margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {simulation.pumps.map((item) => (
                            <tr key={item.fleet_unit_id}>
                              <th>
                                {item.physical_identifier}
                                <small>{item.candidate_site_name}</small>
                              </th>
                              <td>
                                {item.engine_speed_rpm} / {item.pump_speed_rpm.toFixed(0)}
                              </td>
                              <td>{formatSiValue(item.flow_m3_s, 'flowRate')}</td>
                              <td>{formatSiValue(item.head_gain_m, 'head')}</td>
                              <td>
                                {item.brake_power_w === null
                                  ? 'not evaluated'
                                  : formatSiValue(item.brake_power_w, 'power')}
                              </td>
                              <td>
                                {item.npsha_m === null
                                  ? 'not evaluated'
                                  : formatSiValue(item.npsha_m, 'head')}{' '}
                                /{' '}
                                {item.npshr_m === null
                                  ? 'not evaluated'
                                  : formatSiValue(item.npshr_m, 'head')}{' '}
                                /{' '}
                                {item.npsh_margin_m === null
                                  ? 'not evaluated'
                                  : formatSiValue(item.npsh_margin_m, 'head')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                <details className="card results-details">
                  <summary>
                    Hydraulic tables
                    <span>
                      {simulation.nodes.length} nodes · {simulation.links.length} links
                    </span>
                  </summary>
                  <div className="results-hydraulic-sections">
                    <section>
                      <h3>Nodes</h3>
                      {simulation.nodes.length === 0 ? (
                        <p>No node results were saved.</p>
                      ) : (
                        <div className="results-table-wrap">
                          <table>
                            <thead>
                              <tr>
                                <th>Node</th>
                                <th>Flow</th>
                                <th>Head</th>
                                <th>Gauge pressure</th>
                              </tr>
                            </thead>
                            <tbody>
                              {simulation.nodes.map((item) => (
                                <tr key={item.node_id}>
                                  <th>{item.node_name}</th>
                                  <td>{formatSiValue(item.demand_flow_m3_s, 'flowRate')}</td>
                                  <td>{formatSiValue(item.hydraulic_head_m, 'head')}</td>
                                  <td>{formatSiValue(item.pressure_gauge_pa, 'pressure')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                    <section>
                      <h3>Links</h3>
                      {simulation.links.length === 0 ? (
                        <p>No link results were saved.</p>
                      ) : (
                        <div className="results-table-wrap">
                          <table>
                            <thead>
                              <tr>
                                <th>Link</th>
                                <th>Flow</th>
                                <th>Velocity</th>
                                <th>Head loss</th>
                              </tr>
                            </thead>
                            <tbody>
                              {simulation.links.map((item) => (
                                <tr key={item.link_id}>
                                  <th>{item.link_name}</th>
                                  <td>{formatSiValue(item.flow_m3_s, 'flowRate')}</td>
                                  <td>{formatSiValue(item.velocity_m_s, 'velocity')}</td>
                                  <td>{formatSiValue(item.headloss_m, 'head')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                  </div>
                </details>
              </>
            )}
          </>
        )}

        {snapshot.optimizations.length === 0 ? null : (
          <Card title="Optimization proof">
            <ul className="results-optimizations">
              {snapshot.optimizations.map((saved) => {
                const run = saved.run;
                const recommendation = run.recommended_solution;
                return (
                  <li key={run.id}>
                    <strong>{optimizationOutcome(run)}</strong>
                    <span>{proofText(run)}</span>
                    <span>Run status: {readableState(run.state)}.</span>
                    <span>
                      Recommended pumps:{' '}
                      {recommendation?.objective.physical_pump_count ?? 'none'} ·{' '}
                      {run.alternatives.length}{' '}
                      {run.alternatives.length === 1
                        ? 'alternative'
                        : 'alternatives'}
                    </span>
                    {recommendation === null ? null : (
                      <SolutionDetails
                        label="Recommended plan"
                        run={run}
                        solution={recommendation}
                      />
                    )}
                    {run.alternatives.length === 0 ? null : (
                      <details>
                        <summary>
                          Alternatives ({run.alternatives.length})
                        </summary>
                        {run.alternatives.map((solution, index) => (
                          <SolutionDetails
                            {...(recommendation === null
                              ? {}
                              : { baseline: recommendation })}
                            key={index}
                            label={`Alternative ${index + 1}`}
                            run={run}
                            solution={solution}
                          />
                        ))}
                      </details>
                    )}
                    {recommendation !== null ||
                    run.rejected_constraints.length === 0 ? null : (
                      <section>
                        <h4>Limiting checks</h4>
                        <ul>
                          {run.rejected_constraints.map((constraint) => (
                            <li key={constraint.code}>
                              {constraint.message} · {constraint.rejection_count}{' '}
                              {constraint.rejection_count === 1
                                ? 'rejection'
                                : 'rejections'}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                    <details>
                      <summary>Proof record</summary>
                      <dl>
                        <div>
                          <dt>Proof state</dt>
                          <dd>{readableState(run.proof_state)}</dd>
                        </div>
                        <div>
                          <dt>Saved</dt>
                          <dd>{saved.saved_at}</dd>
                        </div>
                        <div>
                          <dt>Run hash</dt>
                          <dd>
                            <code>{saved.run_sha256}</code>
                          </dd>
                        </div>
                      </dl>
                    </details>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        <details className="card results-details">
          <summary>Record details</summary>
          <dl className="results-record-details">
            <div>
              <dt>Snapshot captured</dt>
              <dd>{snapshot.captured_at}</dd>
            </div>
            <div>
              <dt>Snapshot equivalence hash</dt>
              <dd>
                <code>{snapshot.equivalence_sha256}</code>
              </dd>
            </div>
            {simulation === null ? null : (
              <>
                <div>
                  <dt>Result captured</dt>
                  <dd>{simulation.captured_at}</dd>
                </div>
                <div>
                  <dt>Run</dt>
                  <dd>{readableState(simulation.state)}</dd>
                </div>
                <div>
                  <dt>Converged</dt>
                  <dd>{simulation.converged ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt>Feasible</dt>
                  <dd>{simulation.feasible ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt>Request hash</dt>
                  <dd>
                    <code>{simulation.request_sha256}</code>
                  </dd>
                </div>
                <div>
                  <dt>Result hash</dt>
                  <dd>
                    <code>{simulation.result_sha256}</code>
                  </dd>
                </div>
                <div>
                  <dt>Saved snapshot hash</dt>
                  <dd>
                    <code>{simulation.snapshot_sha256}</code>
                  </dd>
                </div>
                <div>
                  <dt>Solver</dt>
                  <dd>
                    {simulation.solver_versions.length === 0
                      ? 'not recorded'
                      : simulation.solver_versions
                          .map((item) => `${item.component} ${item.version}`)
                          .join(', ')}
                  </dd>
                </div>
              </>
            )}
          </dl>
        </details>

        <p className="results-disclaimer">
          Design-assist only. Qualified engineering review is required before construction or
          operation.
        </p>
      </div>
    </Page>
  );
}
